import type { NostrEvent } from '@nostrify/nostrify';
import { nip19 } from 'nostr-tools';
import { Link } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { ArrowLeft } from 'lucide-react';
import { SiteHeader } from '@/components/SiteHeader';
import { SiteFooter } from '@/components/SiteFooter';
import { NoteContent } from '@/components/NoteContent';
import { ShareButton } from '@/components/ShareButton';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useEvent } from '@/hooks/useEvent';
import { useAuthor } from '@/hooks/useAuthor';
import { getDisplayName } from '@/lib/getDisplayName';
import { getPostMeta } from '@/lib/postMeta';
import { sanitizeUrl } from '@/lib/sanitizeUrl';

interface PostViewProps {
  id: string;
  author?: string;
}

export function PostView({ id, author }: PostViewProps) {
  const { data: event, isLoading, isError } = useEvent(id, author);

  if (isLoading) {
    return (
      <PageShell>
        <div className="space-y-6">
          <Skeleton className="aspect-[16/9] w-full rounded-xl" />
          <Skeleton className="h-8 w-3/4" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-11/12" />
            <Skeleton className="h-4 w-4/5" />
          </div>
        </div>
      </PageShell>
    );
  }

  if (isError || !event) {
    return (
      <PageShell>
        <div className="rounded-xl border border-dashed border-border py-16 px-8 text-center">
          <p className="mx-auto max-w-sm text-muted-foreground">
            This post couldn't be found. It may not have reached the connected
            relays.
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
          >
            <ArrowLeft className="size-4" />
            Back to all posts
          </Link>
        </div>
      </PageShell>
    );
  }

  return <PostArticle event={event} />;
}

function PostArticle({ event }: { event: NostrEvent }) {
  const meta = getPostMeta(event);
  const authorQuery = useAuthor(event.pubkey);
  const metadata = authorQuery.data?.metadata;
  const authorName = getDisplayName(metadata, event.pubkey);
  const authorNpub = nip19.npubEncode(event.pubkey);
  const picture = metadata?.picture ? sanitizeUrl(metadata.picture) : undefined;

  useSeoMeta({
    title: `${meta.title} · ${authorName}`,
    description: meta.excerpt,
    ogTitle: meta.title,
    ogDescription: meta.excerpt,
    ogImage: meta.video?.poster || meta.image,
  });

  return (
    <PageShell>
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-primary"
      >
        <ArrowLeft className="size-4" />
        All posts
      </Link>

      <article className="mt-6">
        {meta.tag && (
          <p className="text-xs font-semibold uppercase tracking-widest text-accent">
            {meta.tag}
          </p>
        )}
        <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold leading-tight">
          {meta.title}
        </h1>

        {/* Byline */}
        <div className="mt-5 flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <Link to={`/${authorNpub}`} className="shrink-0">
              <Avatar
                size="lg"
                className="size-11 shrink-0 [&>*]:aspect-square [&>*]:size-full"
              >
                {picture && (
                  <AvatarImage
                    src={picture}
                    alt={authorName}
                    className="aspect-square size-full object-cover"
                  />
                )}
                <AvatarFallback>
                  {authorName.slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </Link>
            <div className="min-w-0 text-sm">
              <Link
                to={`/${authorNpub}`}
                className="block truncate font-medium text-foreground hover:text-primary"
              >
                {authorName}
              </Link>
              <p className="text-muted-foreground">{meta.date}</p>
            </div>
          </div>

          <ShareButton title={`${meta.title} · ${authorName}`} text={meta.excerpt} />
        </div>

        {/* Full content (media renders inline within the note content) */}
        <div className="mt-8 text-lg leading-relaxed">
          <NoteContent event={event} />
        </div>

        {/* Bottom share */}
        <div className="mt-10 flex items-center justify-between border-t border-border pt-6">
          <p className="text-sm text-muted-foreground">Enjoyed this post?</p>
          <ShareButton
            title={`${meta.title} · ${authorName}`}
            text={meta.excerpt}
          />
        </div>
      </article>
    </PageShell>
  );
}

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-2xl px-4 py-12 sm:py-16">{children}</main>
      <SiteFooter />
    </div>
  );
}
